/**
 * 이용 고지·동의 (2026-11) — 서버가 고지문과 판정을 소유한다(server/services/consentDocs.js).
 *
 * 클라이언트는 받은 문서를 **텍스트로만** 그리고, 항목별 선택을 서버에 기록한다. 다시 보여 줄지(required)는
 * 로그인·갱신 응답의 `consent`가 정한다 — 예전 localStorage 판정(utils/disclaimer.ts)은 없앴다.
 * 판정 기준: 기록 없음 · 마지막이 거부/철회 · 고지 내용(첨부 보관 기간 포함)이 바뀜 · 동의 후 100일.
 */
import { apiFetch } from './apiClient';
import { getAppVersion } from './appVersion';

/** 로그인·갱신 응답의 판정. 필드가 없으면(undefined) "모름" — 호출부는 아무것도 하지 않는다 */
export interface ConsentState {
  required: boolean;
  /** valid | none | declined | withdrawn | changed | expired | unavailable */
  reason: string;
  documentId?: number;
}

export interface ConsentSection {
  title: string;
  paragraphs: string[];
  bullets: string[];
}

export interface ConsentGroup {
  /** notice(필수 고지) | rules(이용 수칙) | consent(v3의 열람 별도 동의 — v4부터 notice에 합쳤다) */
  key: string;
  title: string;
  sections: ConsentSection[];
}

export interface ConsentItem {
  key: 'notice' | 'rules' | 'audit_view' | string;
  kind: 'check' | 'choice';
  label: string;
  yes?: string;
  no?: string;
}

export interface ConsentDocument {
  documentId: number;
  version: number;
  effectiveDate: string;
  /** 화면 표시용 시행일('2026년 9월') — 옛 서버면 없다 */
  effectiveLabel?: string;
  renderedHash: string;
  reagreeDays: number;
  title: string;
  groups: ConsentGroup[];
  items: ConsentItem[];
  contact: string;
  state?: ConsentState;
}

export type ConsentAction = 'accept' | 'decline' | 'withdraw';
export type ConsentItems = Record<string, boolean>;

export interface ConsentLog {
  id: number;
  documentId: number;
  version: number | null;
  action: ConsentAction;
  items: ConsentItems;
  facts: Record<string, unknown>;
  createdAt: string;
  clientType: string | null;
  appVersion: string | null;
  deviceId: number | null;
  ipAddress: string | null;
  userAgent: string | null;
}

/** 기록 항목 — accept면 셋 다 true(서버 normalizeItems와 같은 목록). audit_view는 v4부터 '동의 후 시작'으로 받는다 */
export const CONSENT_ITEM_KEYS = ['notice', 'rules', 'audit_view'] as const;

export const consentService = {
  getDocument: (): Promise<ConsentDocument> => apiFetch<ConsentDocument>('/consent/chat-notice'),

  /** 기록 — 실패는 throw(ApiError: 409 CONSENT_CHANGED면 호출부가 문서를 다시 받는다) */
  submit: async (
    doc: Pick<ConsentDocument, 'documentId' | 'renderedHash'>,
    action: ConsentAction,
    items: ConsentItems
  ): Promise<{ consent: ConsentState; log: ConsentLog | null }> => {
    const appVersion = await getAppVersion().catch(() => undefined);
    return apiFetch('/consent/chat-notice', {
      method: 'POST',
      body: JSON.stringify({ documentId: doc.documentId, renderedHash: doc.renderedHash, action, items, appVersion }),
    });
  },

  myLogs: async (): Promise<ConsentLog[]> => {
    const r = await apiFetch<{ logs: ConsentLog[] }>('/consent/my-logs');
    return r.logs || [];
  },
};

/** 기록 한 줄을 사람이 읽는 말로 — 내 동의 내역·관리자 화면 공용 */
export function describeConsentAction(action: string): string {
  if (action === 'accept') return '동의';
  if (action === 'decline') return '동의하지 않음';
  if (action === 'withdraw') return '동의 철회';
  return action;
}

/** 기기 표시 — 앱 버전이 숫자면 PC 앱, 'web'이면 클라이언트 종류로 */
export function describeConsentDevice(clientType: string | null, appVersion: string | null): string {
  if (appVersion && /^\d+\.\d+/.test(appVersion)) return `PC 앱 ${appVersion}`;
  if (clientType === 'mobile') return '휴대폰';
  if (appVersion === 'web' || clientType === 'desktop') return '웹 브라우저';
  return '알 수 없음';
}
